"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Loader as Loader2, Mail, Phone, Link as LinkIcon } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">
          {successMessage}
        </h3>
        <p className="text-muted-foreground max-w-md">
          We have received your information and will be in touch shortly to discuss your business architecture.
        </p>
      </div>;
  }
  const inputStyles = "border-x-transparent border-t-transparent border-b-border bg-transparent rounded-md shadow-xs focus-visible:border-x-transparent focus-visible:border-t-transparent focus-visible:border-b-primary focus-visible:ring-0 px-2 rounded-b-none";
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-8">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm font-medium">{submitError}</p>
          </div>}

        <div className="pt-4">
          <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-medium rounded-md shadow-md transition-all hover:shadow-lg">
            {isSubmitting ? <>
                <Loader2 className="animate-spin mr-2 size-5" />
                Submitting...
              </> : "Request Consultation"}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function ScaleStrategyContact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact" className="bg-background py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden opacity-30">
        <div className="absolute top-0 left-[10%] w-[1px] h-full bg-border/50" />
        <div className="absolute top-0 left-[50%] w-[1px] h-full bg-border/50" />
        <div className="absolute top-0 right-[10%] w-[1px] h-full bg-border/50" />
        <div className="absolute top-[20%] left-0 w-full h-[1px] bg-border/50" />
        <div className="absolute bottom-[20%] left-0 w-full h-[1px] bg-border/50" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight">
            Let's Architect Your Growth
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans max-w-2xl mx-auto">
            Ready to optimize your financial systems? Connect with our advisory team to build the blueprint for your scale.
          </p>
        </div>

        <div ref={ref} className={`max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="bg-card rounded-xl p-8 md:p-12 border border-border/50 flex flex-col justify-between space-y-12">
            <div className="space-y-8">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our partners are ready to review your current financial architecture and identify the structural optimizations needed for your next phase of growth.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4 group">
                  <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-primary/60">Email Us</p>
                    <p className="text-lg font-medium text-foreground">infoscalebase@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 group">
                  <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-primary/60">Call Directly</p>
                    <p className="text-lg font-medium text-foreground">(780) 200-0284</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border/50">
              <h4 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-6">Connect With Us</h4>
              <div className="flex flex-wrap gap-6">
                <Link to="https://www.instagram.com/scalebasetax/" newTab className="flex items-center gap-2 text-foreground hover:text-primary transition-colors cursor-pointer">
                  <LinkIcon className="size-4 text-primary" />
                  <span className="text-sm font-medium">Instagram</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-card p-8 md:p-12 rounded-xl shadow-xl border border-border/50 relative">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/30 rounded-tl-xl" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary/30 rounded-tr-xl" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary/30 rounded-bl-xl" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary/30 rounded-br-xl" />

            <FormProvider formId="contact-form-scale-strategy" sectionName="ScaleStrategyContact" defaultValues={{
            fullName: "",
            email: "",
            phone: ""
          }} submitText="Request Consultation" successMessage="Blueprint Initiated.">
              <ContactFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}